/**
 * Intranet Osasco Plaza — src/modules/comunicados.jsx
 * Telas do módulo "Comunicados" (avisos internos). Extraído de app.jsx —
 * ver DOC/ADR/0011.
 */

import { COMUNICADO_CATEGORIAS, Icon, hasPerm, relativeTime } from './shared.jsx';
const { useState, useEffect, useRef } = React;


/* ════════════════════════════════════════════════════════════════
   MINI COMUNICADO SCREEN
   ════════════════════════════════════════════════════════════════ */
export function MiniComunicadoScreen({ user, onChange }) {
  const [lista, setLista]       = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modal, setModal]       = useState(null); // null | 'new' | objeto
  const [deleteTarget, setDel]  = useState(null);
  const [titulo, setTitulo]     = useState('');
  const [mensagem, setMensagem] = useState('');
  const [ativo, setAtivo]       = useState(true);
  const [err, setErr]           = useState('');
  const [busy, setBusy]         = useState(false);

  const refresh = () => {
    setCarregando(true);
    API.comunicados.mini.listar()
      .then(d => setLista(d.miniComunicados || []))
      .catch(() => setLista([]))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setTitulo(''); setMensagem(''); setAtivo(true); setErr(''); setModal('new');
  };
  const openEdit = (mc) => {
    setTitulo(mc.titulo); setMensagem(mc.mensagem); setAtivo(mc.ativo); setErr(''); setModal(mc);
  };

  const save = async () => {
    if (!titulo.trim())   { setErr('Informe o título.'); return; }
    if (!mensagem.trim()) { setErr('Informe a mensagem.'); return; }
    setBusy(true);
    try {
      const dados = { titulo: titulo.trim(), mensagem: mensagem.trim(), ativo };
      if (modal === 'new') await API.comunicados.mini.criar(dados);
      else                 await API.comunicados.mini.editar(modal.id, dados);
      refresh();
      onChange && onChange();
      setModal(null);
    } catch (e) {
      setErr(e.message || 'Falha ao salvar.');
    }
    setBusy(false);
  };

  const toggleAtivo = async (mc) => {
    try {
      await API.comunicados.mini.ativar(mc.id);
      refresh();
      onChange && onChange();
    } catch { /* a lista não muda se falhar; usuário tenta de novo */ }
  };

  const confirmDelete = async () => {
    try {
      await API.comunicados.mini.remover(deleteTarget.id);
      refresh();
      onChange && onChange();
    } catch { /* mantém o modal aberto implicitamente ao não limpar deleteTarget seria confuso; segue e fecha */ }
    setDel(null);
  };

  const canEdit = hasPerm(user,'feed','create');

  return (
    <div className="main-pad">
      {/* Modal novo/editar */}
      {modal !== null && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <h3 style={{margin:0,fontSize:16}}>{modal==='new'?'Novo Mini Comunicado':'Editar Mini Comunicado'}</h3>
              <button className="icon-btn" onClick={()=>setModal(null)}><Icon name="x" size={16}/></button>
            </div>
            <p style={{fontSize:12,color:'var(--ink-3)',marginBottom:18}}>
              {modal==='new'?'Crie uma mensagem de destaque para o canto inferior esquerdo da sidebar.':'Edite o mini comunicado existente.'}
            </p>
            <div className="field">
              <label>Título <span className="req">*</span></label>
              <input value={titulo} onChange={e=>setTitulo(e.target.value)} placeholder="Ex.: Festival Gastronômico" maxLength={60} autoFocus/>
            </div>
            <div className="field">
              <label>Mensagem <span className="req">*</span></label>
              <textarea rows={3} value={mensagem} onChange={e=>setMensagem(e.target.value)} placeholder="Ex.: Inscreva sua loja como parceira até 08/05." maxLength={160}/>
              <div className="hint">{mensagem.length}/160 caracteres</div>
            </div>
            <div className="field" style={{display:'flex',alignItems:'center',gap:10,marginBottom:0}}>
              <input type="checkbox" id="mc-ativo" checked={ativo} onChange={e=>setAtivo(e.target.checked)} style={{width:'auto',height:'auto'}}/>
              <label htmlFor="mc-ativo" style={{margin:0,cursor:'pointer'}}>✅ Exibir na sidebar (desativa os demais)</label>
            </div>
            {err && <div style={{color:'var(--err)',fontSize:12,margin:'12px 0',padding:'8px 12px',background:'var(--err-bg)',borderRadius:8}}>{err}</div>}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:20}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Salvando...':'Salvar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal excluir */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={()=>setDel(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:380}}>
            <h3 style={{marginBottom:10}}>Excluir mini comunicado?</h3>
            <p style={{fontSize:13,color:'var(--ink-2)',marginBottom:20}}>
              "<strong>{deleteTarget.titulo}</strong>" será removido permanentemente.
            </p>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={()=>setDel(null)}>Cancelar</button>
              <button className="btn btn-danger" onClick={confirmDelete}>Excluir</button>
            </div>
          </div>
        </div>
      )}

      <div className="page-title-row">
        <div className="page-title">
          <h2>Mini Comunicado</h2>
          <p>Gerencie a mensagem de destaque exibida no canto inferior esquerdo da sidebar.</p>
        </div>
        {canEdit && <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/>Novo Mini Comunicado</button>}
      </div>

      {carregando ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>Carregando...</div>
      ) : lista.length === 0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="bell" size={32} style={{margin:'0 auto 12px',display:'block',opacity:0.3}}/>
          <div style={{fontWeight:600,color:'var(--ink-2)',marginBottom:6}}>Nenhum mini comunicado cadastrado.</div>
          {canEdit && <p style={{fontSize:13}}>Clique em <strong>Novo Mini Comunicado</strong> para criar um.</p>}
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {lista.map(mc => (
            <div key={mc.id} className="card" style={{display:'flex',alignItems:'center',gap:16,padding:'14px 18px'}}>
              {/* Preview */}
              <div style={{width:10,height:10,borderRadius:'50%',background:mc.ativo?'var(--ok)':'var(--ink-4)',flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14,color:'var(--ink)',marginBottom:2}}>{mc.titulo}</div>
                <div style={{fontSize:12,color:'var(--ink-3)',lineHeight:1.4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{mc.mensagem}</div>
                <div style={{fontSize:10,color:'var(--ink-4)',marginTop:4,fontFamily:'JetBrains Mono,monospace'}}>
                  {mc.criadoEm ? new Date(mc.criadoEm).toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}) : ''}
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                <span className={`status-pill ${mc.ativo?'ok':'muted'}`}>
                  <span className="ind"/>
                  {mc.ativo ? 'Ativo' : 'Inativo'}
                </span>
                {canEdit && <>
                  <button className="btn btn-ghost btn-sm" title={mc.ativo?'Desativar':'Ativar'} onClick={()=>toggleAtivo(mc)}>
                    <Icon name={mc.ativo?'x':'check'} size={13}/>
                    {mc.ativo ? 'Desativar' : 'Ativar'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(mc)}>
                    <Icon name="edit" size={13}/> Editar
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={()=>setDel(mc)}>
                    <Icon name="trash" size={13}/>
                  </button>
                </>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview da sidebar */}
      {lista.some(m=>m.ativo) && (
        <div className="card" style={{marginTop:20}}>
          <div className="card-head"><h3>Preview — como aparece na sidebar</h3></div>
          <div style={{background:'var(--gold-soft)',borderRadius:12,padding:12,display:'flex',flexDirection:'column',gap:6,maxWidth:220}}>
            <Icon name="sparkle" size={14} style={{color:'var(--gold-2)'}}/>
            <h4 style={{fontSize:12,fontWeight:600,margin:0,color:'#5e4a16'}}>{lista.find(m=>m.ativo)?.titulo}</h4>
            <p style={{fontSize:11,color:'#7c6326',margin:0,lineHeight:1.4}}>{lista.find(m=>m.ativo)?.mensagem}</p>
            <button style={{marginTop:4,padding:'6px 10px',background:'var(--navy)',color:'var(--gold)',border:0,borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'inherit',alignSelf:'flex-start'}}>Gerenciar</button>
          </div>
        </div>
      )}
    </div>
  );
}


export function ComunicadoModal({ comunicado, currentUser, onSave, onClose }) {
  const isNew = !comunicado;
  const [titulo,  setTitulo]  = useState(comunicado?.titulo   || '');
  const [corpo,   setCorpo]   = useState(comunicado?.corpo    || '');
  const [categ,   setCateg]   = useState(comunicado?.categoria || 'Geral');
  const [tags,    setTags]    = useState((comunicado?.tags||[]).join(', '));
  const [fixado,  setFixado]  = useState(comunicado?.fixado  || false);
  const [exibicao, setExibicao] = useState(comunicado?.exibicao || 'feed');
  const [err,     setErr]     = useState('');
  const [busy,    setBusy]    = useState(false);

  const submit = async () => {
    if (!titulo.trim()) { setErr('Informe o título do comunicado.'); return; }
    if (!corpo.trim())  { setErr('Informe o conteúdo do comunicado.'); return; }
    const tagsArr = tags.split(',').map(t=>t.trim().replace(/^#/,'')).filter(Boolean);
    const dados = {
      titulo: titulo.trim(), corpo: corpo.trim(), categoria: categ,
      tags: tagsArr, fixado, exibicao,
      autorCargo: currentUser?.cargo || '',
    };
    setBusy(true); setErr('');
    try {
      if (isNew) await API.comunicados.criar(dados);
      else       await API.comunicados.editar(comunicado.id, dados);
      onSave();
      onClose();
    } catch (e) {
      setErr(e.message || 'Falha ao salvar o comunicado.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-card" style={{ maxWidth:620, width:'95%' }}>
        <button className="close-x" onClick={onClose}><Icon name="x" size={16}/></button>
        <h3>{isNew ? '📢 Novo Comunicado' : '✏️ Editar Comunicado'}</h3>
        <div style={{ fontSize:12, color:'var(--ink-3)', marginBottom:16 }}>
          {isNew ? 'Crie um comunicado visível para todos os colaboradores.' : 'Edite o comunicado existente.'}
        </div>
        <div className="divider"/>

        <div className="field">
          <label>Título <span className="req">*</span></label>
          <input type="text" value={titulo} onChange={e=>setTitulo(e.target.value)} placeholder="Ex.: Reunião geral — 15/05 às 10h"/>
        </div>
        <div className="field">
          <label>Conteúdo <span className="req">*</span></label>
          <textarea rows={5} value={corpo} onChange={e=>setCorpo(e.target.value)} placeholder="Escreva o comunicado aqui..."/>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div className="field">
            <label>Categoria</label>
            <select value={categ} onChange={e=>setCateg(e.target.value)}>
              {COMUNICADO_CATEGORIAS.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Tags <span style={{ fontWeight:400, color:'var(--ink-4)' }}>(separadas por vírgula)</span></label>
            <input type="text" value={tags} onChange={e=>setTags(e.target.value)} placeholder="ex: urgente, reunião, todos"/>
          </div>
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', marginBottom:16 }}>
          <input type="checkbox" checked={fixado} onChange={e=>setFixado(e.target.checked)} style={{ accentColor:'var(--navy)', width:15, height:15 }}/>
          <span>📌 Fixar este comunicado no topo do feed</span>
        </label>

        <div className="field" style={{ marginBottom:16 }}>
          <label>📍 Exibir comunicado em</label>
          <div style={{ display:'flex', gap:10, marginTop:6 }}>
            {[
              { val:'feed',  label:'📋 Feed principal', desc:'Aparece na tela principal após login' },
              { val:'login', label:'🔐 Tela de login',  desc:'Aparece no carrossel antes do login' },
              { val:'ambos', label:'🌐 Ambos',          desc:'Feed principal e tela de login' },
            ].map(opt => (
              <label key={opt.val} style={{
                display:'flex', flexDirection:'column', gap:3, padding:'10px 14px',
                border: exibicao === opt.val ? '2px solid var(--navy)' : '1px solid var(--line)',
                borderRadius:10, cursor:'pointer', flex:1,
                background: exibicao === opt.val ? 'rgba(10,20,40,0.06)' : 'transparent',
                transition:'all 0.15s',
              }}>
                <input type="radio" name="exibicao" value={opt.val}
                  checked={exibicao === opt.val}
                  onChange={() => setExibicao(opt.val)}
                  style={{ display:'none' }}/>
                <span style={{ fontSize:12, fontWeight:600 }}>{opt.label}</span>
                <span style={{ fontSize:11, color:'var(--ink-3)' }}>{opt.desc}</span>
              </label>
            ))}
          </div>
        </div>

        {err && <div style={{ color:'var(--err)', fontSize:12, marginBottom:12, padding:'8px 12px', background:'var(--err-bg)', borderRadius:8 }}>{err}</div>}
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}><Icon name="check" size={13}/> {busy ? 'Salvando...' : (isNew ? 'Publicar' : 'Salvar alterações')}</button>
        </div>
      </div>
    </div>
  );
}


export function Feed({ mobile, user }) {
  /* `canManage` controla criar/editar/fixar (ação `create`, igual ao
     protótipo). `canExcluir` é NOVO: exige a ação `delete` de verdade — por
     padrão só admin (ver DOC/ADR/0007). No protótipo o mesmo botão Excluir
     usava `canManage`, então gestor apagava comunicado; aqui gestor edita
     mas quem apaga é admin. */
  const canManage  = hasPerm(user, 'feed', 'create');
  const canExcluir = hasPerm(user, 'feed', 'delete');

  const [items, setItems]           = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modal, setModal]           = useState(null);
  const [delConf, setDelConf]       = useState(null);
  const [leitoresDe, setLeitoresDe] = useState(null); // id do comunicado, ou null
  const [leitoresDados, setLeitoresDados] = useState(null);

  const refresh = () => {
    setCarregando(true);
    API.comunicados.listar()
      .then(d => {
        setItems(d.comunicados || []);
        /* Marca como lido cada item ainda não lido por este usuário. É
           "fogo e esquece": se falhar, o pior caso é a leitura não contar
           ainda — a próxima visita ao Feed tenta de novo. */
        for (const it of (d.comunicados || [])) {
          if (!it.euLi) API.comunicados.marcarLida(it.id).catch(() => {});
        }
      })
      .catch(() => setItems([]))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { refresh(); }, []);

  const curtir = async (id) => {
    try {
      const r = await API.comunicados.curtir(id);
      setItems(prev => prev.map(it => it.id === id ? { ...it, euCurti: r.ativo, totalCurtidas: r.total } : it));
    } catch { /* estado local não muda se o servidor recusar */ }
  };

  const salvar = async (id) => {
    try {
      const r = await API.comunicados.salvar(id);
      setItems(prev => prev.map(it => it.id === id ? { ...it, euSalvei: r.ativo } : it));
    } catch {}
  };

  const excluir = async (id) => {
    try {
      await API.comunicados.remover(id);
      setDelConf(null);
      refresh();
    } catch (e) {
      setDelConf(null);
      alert(e.message || 'Falha ao excluir.');
    }
  };

  const abrirLeitores = (id) => {
    setLeitoresDe(id);
    setLeitoresDados(null);
    API.comunicados.leitores(id).then(setLeitoresDados).catch(() => setLeitoresDados({ erro: true }));
  };

  const categColor = (c) => {
    const map = { Geral:'var(--ink-3)', Operacional:'var(--info)', RH:'var(--ok)', Segurança:'var(--err)',
      Manutenção:'var(--warn)', Comercial:'var(--gold-2)', TI:'var(--navy)', Evento:'#7c3aed' };
    return map[c] || 'var(--ink-3)';
  };

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>Comunicados</h2>
          <p>{items.length} publicação{items.length!==1?'s':''}</p>
        </div>
        {canManage && (
          <button className="btn btn-gold" onClick={()=>setModal('new')}>
            <Icon name="plus" size={13}/> Novo Comunicado
          </button>
        )}
      </div>

      {carregando && (
        <div className="card" style={{ textAlign:'center', padding:48, color:'var(--ink-3)' }}>Carregando...</div>
      )}

      {!carregando && items.length === 0 && (
        <div className="card" style={{ textAlign:'center', padding:48 }}>
          <Icon name="megaphone" size={36} style={{ color:'var(--ink-4)', display:'block', margin:'0 auto 12px' }}/>
          <div style={{ fontWeight:600, color:'var(--ink-2)', marginBottom:6 }}>Nenhum comunicado publicado ainda.</div>
          {canManage && <div style={{ fontSize:13, color:'var(--ink-3)' }}>Clique em <b>Novo Comunicado</b> para criar o primeiro.</div>}
        </div>
      )}

      {!carregando && items.map(it=>(
        <div className="feed-card" key={it.id} style={{ borderLeft: it.fixado ? '4px solid var(--gold)' : undefined }}>
          {it.fixado && (
            <div style={{ fontSize:11, fontWeight:600, color:'var(--gold-2)', marginBottom:8, display:'flex', alignItems:'center', gap:5 }}>
              📌 Comunicado fixado
            </div>
          )}
          {canManage && it.exibicao && it.exibicao !== 'feed' && (
            <div style={{ fontSize:11, fontWeight:600, color: it.exibicao==='login'?'var(--info)':'var(--ok)', marginBottom:6, display:'flex', alignItems:'center', gap:4 }}>
              {it.exibicao==='login' ? '🔐 Visível na tela de login' : '🌐 Feed + tela de login'}
            </div>
          )}
          <div className="feed-head">
            <div className="av">{(it.autorNome||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase()}</div>
            <div>
              <div className="who">{it.autorNome}</div>
              <div className="when">{it.autorCargo || 'Gestão'} · {relativeTime(new Date(it.criadoEm).getTime())}{it.editadoEm ? ' · editado' : ''}</div>
            </div>
            <span style={{ marginLeft:'auto', fontSize:11, fontWeight:700, color:'white', background:categColor(it.categoria), padding:'3px 9px', borderRadius:999 }}>
              {it.categoria||'Geral'}
            </span>
          </div>

          <h4>{it.titulo}</h4>
          <p className="body" style={{ whiteSpace:'pre-wrap' }}>{it.corpo}</p>

          {it.tags && it.tags.length > 0 && (
            <div className="feed-tags">{it.tags.map(t=><span key={t}>#{t}</span>)}</div>
          )}

          <div className="feed-actions">
            <button className={it.euCurti?'liked':''} onClick={()=>curtir(it.id)}>
              <Icon name="heart" size={14}/> {it.totalCurtidas||0}
            </button>
            <button className={it.euSalvei?'saved':''} onClick={()=>salvar(it.id)}>
              <Icon name="bookmark" size={14}/> {it.euSalvei?'Salvo':'Salvar'}
            </button>
            {canManage && (
              <button style={{ color:'var(--ink-3)' }} onClick={()=>abrirLeitores(it.id)} title="Quem já leu">
                <Icon name="users" size={14}/> {it.totalLeituras||0} leram
              </button>
            )}
            {canManage && (
              <>
                <button style={{ marginLeft: canManage ? undefined : 'auto', color:'var(--info)' }} onClick={()=>setModal(it)}>
                  <Icon name="tools" size={14}/> Editar
                </button>
                {canExcluir && (
                  <button style={{ color:'var(--err)' }} onClick={()=>setDelConf(it)}>
                    <Icon name="x" size={14}/> Excluir
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      ))}

      {/* Modal criar / editar */}
      {modal && (
        <ComunicadoModal
          comunicado={modal==='new' ? null : modal}
          currentUser={user}
          onSave={refresh}
          onClose={()=>setModal(null)}
        />
      )}

      {/* Quem já leu */}
      {leitoresDe && (
        <div className="modal-overlay" onClick={()=>setLeitoresDe(null)}>
          <div className="modal-card" style={{ maxWidth:420 }} onClick={e=>e.stopPropagation()}>
            <button className="close-x" onClick={()=>setLeitoresDe(null)}><Icon name="x" size={16}/></button>
            <h3 style={{ marginBottom:12 }}>Quem já leu</h3>
            {!leitoresDados && <div style={{ color:'var(--ink-3)', fontSize:13 }}>Carregando...</div>}
            {leitoresDados?.erro && <div style={{ color:'var(--err)', fontSize:13 }}>Falha ao carregar.</div>}
            {leitoresDados && !leitoresDados.erro && (
              <>
                <div style={{ fontSize:12, color:'var(--ink-3)', marginBottom:10 }}>
                  {leitoresDados.leram.length} de {leitoresDados.total} colaboradores já leram.
                </div>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--ok)', textTransform:'uppercase', marginBottom:4 }}>Leram</div>
                {leitoresDados.leram.length === 0 && <div style={{ fontSize:12, color:'var(--ink-4)', marginBottom:10 }}>Ninguém ainda.</div>}
                {leitoresDados.leram.map(p => (
                  <div key={p.id} style={{ fontSize:13, padding:'4px 0' }}>{p.nome}</div>
                ))}
                <div style={{ fontSize:11, fontWeight:700, color:'var(--warn)', textTransform:'uppercase', margin:'12px 0 4px' }}>Ainda não leram</div>
                {leitoresDados.naoLeram.length === 0 && <div style={{ fontSize:12, color:'var(--ink-4)' }}>Todo mundo já leu.</div>}
                {leitoresDados.naoLeram.map(p => (
                  <div key={p.id} style={{ fontSize:13, padding:'4px 0', color:'var(--ink-3)' }}>{p.nome}</div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      {delConf && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth:420, textAlign:'center' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🗑️</div>
            <h3>Excluir comunicado?</h3>
            <p style={{ fontSize:13, color:'var(--ink-3)', margin:'10px 0 20px', lineHeight:1.5 }}>
              "<b>{delConf.titulo}</b>" será removido permanentemente do feed.
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-ghost" style={{ flex:1 }} onClick={()=>setDelConf(null)}>Cancelar</button>
              <button className="btn btn-danger" style={{ flex:1 }} onClick={()=>excluir(delConf.id)}>
                <Icon name="x" size={13}/> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
